import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';
import { Button } from '@kizen/kds/Button';
import { useWYSIWYGAIContext } from '../../../WYSIWYG';
import {
  TextEditorToolbarSection,
  ToolbarIconMenuContainer,
  ToolbarIconMenu,
  ToolbarIconMenuList,
  ToolbarIconMenuListItem,
} from '../../styles';
import { useGridLikeFlexChildProps } from 'components/StandaloneToolBar/components/GridLikeFlex';
import { useMarkdownEditor } from 'components/WYSIWYG/MarkdownEditor/context';

const buildOptions = (t) => [
  { value: 'complete', label: t('Complete') },
  { value: 'extend', label: t('Extend') },
  { value: 'rephrase', label: t('Rephrase') },
  { value: 'summarize', label: t('Summarize') },
  {
    value: 'spelling_and_grammar',
    label: `${t('Spelling')} & ${t('Grammar')}`,
  },
];

export const AIToolbarSection = () => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [ref, props] = useGridLikeFlexChildProps();
  const clickAwayRef = useRef();
  useClickAway(clickAwayRef, () => setMenuOpen(false));
  const { performRequest } = useWYSIWYGAIContext();
  const { editor, subscribeToSelectionUpdate } = useMarkdownEditor();

  const [{ aiEnabled }, setState] = useState(() => ({
    aiEnabled: false,
  }));

  const options = useMemo(() => {
    return buildOptions(t);
  }, [t]);

  useEffect(() => {
    return subscribeToSelectionUpdate('ai-toolbar-section', ({ editor }) => {
      if (!editor) {
        return null;
      }
      setState({
        aiEnabled: !editor.state.selection.empty,
      });
    });
  }, [subscribeToSelectionUpdate]);

  if (!editor) {
    return null;
  }

  return (
    <TextEditorToolbarSection ref={ref} {...props}>
      <ToolbarIconMenuContainer ref={clickAwayRef}>
        <div
          style={{
            color: aiEnabled
              ? 'var(--color-font-primary)'
              : 'var(--color-background-medium)',
          }}
        >
          <Button
            variant="text"
            color="inherit"
            rightIcon="text-ai"
            onClick={() => setMenuOpen((x) => !x)}
            disabled={!aiEnabled}
          />
        </div>
        {menuOpen && (
          <ToolbarIconMenu top={40}>
            <ToolbarIconMenuList>
              {options.map(({ label, value }) => (
                <ToolbarIconMenuListItem
                  key={value}
                  onClick={() => {
                    setMenuOpen(false);
                    performRequest(value, editor);
                  }}
                >
                  {label}
                </ToolbarIconMenuListItem>
              ))}
            </ToolbarIconMenuList>
          </ToolbarIconMenu>
        )}
      </ToolbarIconMenuContainer>
    </TextEditorToolbarSection>
  );
};
